import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Wallet, CreditCard, AlertTriangle, Check, X, Search, RefreshCw, 
  DollarSign, TrendingUp, TrendingDown, User, Building, Plus, Minus
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminZorki() {
  const [wallets, setWallets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState("");
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [adjustDialog, setAdjustDialog] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState(0);
  const [adjustReason, setAdjustReason] = useState("");
  const [adjusting, setAdjusting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [walletsRes, txRes] = await Promise.all([
        axios.get(`${API}/admin/zorki/all-wallets`),
        axios.get(`${API}/admin/zorki/all-transactions`),
      ]);
      setWallets(walletsRes.data);
      setTransactions(txRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Statystyki
  const totalBalance = wallets.reduce((sum, w) => sum + (w.balance || 0), 0);
  const totalCreditUsed = wallets.reduce((sum, w) => sum + (w.credit_used || 0), 0);
  const overdueWallets = wallets.filter(w => w.credit_overdue);
  const totalOverdue = overdueWallets.reduce((sum, w) => sum + (w.credit_used || 0), 0);

  // Filtrowanie
  const filteredWallets = wallets.filter(w => {
    const q = searchQ.toLowerCase();
    return (w.user_email?.toLowerCase().includes(q) || 
            w.user_name?.toLowerCase().includes(q) ||
            w.company_name?.toLowerCase().includes(q));
  });

  const filteredTransactions = transactions.filter(t => {
    const q = searchQ.toLowerCase();
    return (t.user_email?.toLowerCase().includes(q) || 
            t.user_name?.toLowerCase().includes(q) ||
            t.description?.toLowerCase().includes(q));
  });

  // Oznacz kredyt jako splacony
  const handleMarkPaid = async (userId) => {
    try {
      await axios.post(`${API}/admin/zorki/mark-credit-paid/${userId}`, {});
      toast.success("Kredyt oznaczony jako splacony");
      fetchData();
    } catch (err) {
      toast.error("Blad");
    }
  };

  // Dodaj/odejmij saldo
  const handleAdjust = async () => {
    if (!selectedWallet || adjustAmount === 0) return;
    setAdjusting(true);
    try {
      await axios.post(`${API}/admin/zorki/add-balance/${selectedWallet.user_id}`, {
        amount: adjustAmount,
        reason: adjustReason || `Korekta admina: ${adjustAmount > 0 ? '+' : ''}${adjustAmount} PLN`
      });
      toast.success(`Saldo zmienione o ${adjustAmount} PLN`);
      setAdjustDialog(false);
      setAdjustAmount(0);
      setAdjustReason("");
      fetchData();
    } catch (err) {
      toast.error("Blad");
    } finally {
      setAdjusting(false);
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      topup: { label: "Doladowanie", color: "bg-emerald-100 text-emerald-700" },
      spend: { label: "Wydatek", color: "bg-red-100 text-red-700" },
      credit_granted: { label: "Kredyt przyznany", color: "bg-blue-100 text-blue-700" },
      credit_payment: { label: "Splacenie kredytu", color: "bg-purple-100 text-purple-700" },
      credit_payment_admin: { label: "Splacenie (admin)", color: "bg-purple-100 text-purple-700" },
      admin_adjustment: { label: "Korekta admina", color: "bg-amber-100 text-amber-700" },
      referral: { label: "Bonus polecenia", color: "bg-pink-100 text-pink-700" },
    };
    return labels[type] || { label: type, color: "bg-slate-100 text-slate-700" };
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Zarzadzanie Zorki</h1>
          <p className="text-sm text-muted-foreground">Historia transakcji, portfele i kredyty kupieckie</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Odswiez
        </Button>
      </div>

      {/* Statystyki */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="rounded-xl border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Wallet className="w-8 h-8 text-emerald-600" />
              <div>
                <p className="text-2xl font-bold text-emerald-700">{totalBalance} PLN</p>
                <p className="text-xs text-emerald-600">Laczne salda</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CreditCard className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-700">{totalCreditUsed} PLN</p>
                <p className="text-xs text-blue-600">Kredyt wykorzystany</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={`rounded-xl ${totalOverdue > 0 ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className={`w-8 h-8 ${totalOverdue > 0 ? 'text-red-600' : 'text-slate-400'}`} />
              <div>
                <p className={`text-2xl font-bold ${totalOverdue > 0 ? 'text-red-700' : 'text-slate-500'}`}>{totalOverdue} PLN</p>
                <p className={`text-xs ${totalOverdue > 0 ? 'text-red-600' : 'text-slate-500'}`}>Zaleglosci ({overdueWallets.length})</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-slate-600" />
              <div>
                <p className="text-2xl font-bold text-slate-700">{transactions.length}</p>
                <p className="text-xs text-slate-500">Transakcji</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Wyszukiwanie */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input 
          value={searchQ} 
          onChange={(e) => setSearchQ(e.target.value)}
          placeholder="Szukaj po email, nazwie firmy..."
          className="pl-10"
        />
      </div>

      <Tabs defaultValue="wallets" className="space-y-4">
        <TabsList>
          <TabsTrigger value="wallets">Portfele ({filteredWallets.length})</TabsTrigger>
          <TabsTrigger value="transactions">Transakcje ({filteredTransactions.length})</TabsTrigger>
          <TabsTrigger value="overdue" className={overdueWallets.length > 0 ? "text-red-600" : ""}>
            Zaleglosci ({overdueWallets.length})
          </TabsTrigger>
        </TabsList>

        {/* PORTFELE */}
        <TabsContent value="wallets">
          <Card className="rounded-xl">
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Uzytkownik</TableHead>
                    <TableHead>Firma / NIP</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                    <TableHead className="text-right">Kredyt limit</TableHead>
                    <TableHead className="text-right">Kredyt uzyty</TableHead>
                    <TableHead className="text-right">Dostepne</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Akcje</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWallets.map((w) => (
                    <TableRow key={w.user_id} className={w.credit_overdue ? "bg-red-50" : ""}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <div>
                            <p className="text-sm font-medium">{w.user_name || "—"}</p>
                            <p className="text-xs text-slate-500">{w.user_email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{w.company_name || "—"}</p>
                          <p className="text-xs text-slate-500">{w.nip || "—"}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold">{w.balance || 0}</TableCell>
                      <TableCell className="text-right font-mono">{w.credit_limit || 0}</TableCell>
                      <TableCell className={`text-right font-mono ${w.credit_used > 0 ? 'text-amber-600 font-bold' : ''}`}>
                        {w.credit_used || 0}
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold text-emerald-600">
                        {(w.balance || 0) + (w.credit_limit || 0) - (w.credit_used || 0)}
                      </TableCell>
                      <TableCell>
                        {w.credit_overdue ? (
                          <Badge className="bg-red-100 text-red-700 border-red-200">
                            <AlertTriangle className="w-3 h-3 mr-1" /> Zaleglosc {w.credit_overdue_days}d
                          </Badge>
                        ) : w.credit_used > 0 ? (
                          <Badge className="bg-amber-100 text-amber-700">Kredyt aktywny</Badge>
                        ) : (
                          <Badge variant="outline">OK</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-7 text-xs"
                            onClick={() => { setSelectedWallet(w); setAdjustDialog(true); }}
                          >
                            <Plus className="w-3 h-3 mr-1" /> Dodaj
                          </Button>
                          {w.credit_used > 0 && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-7 text-xs text-emerald-600 border-emerald-300"
                              onClick={() => handleMarkPaid(w.user_id)}
                            >
                              <Check className="w-3 h-3 mr-1" /> Splacone
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </Card>
        </TabsContent>

        {/* TRANSAKCJE */}
        <TabsContent value="transactions">
          <Card className="rounded-xl">
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Uzytkownik</TableHead>
                    <TableHead>Typ</TableHead>
                    <TableHead>Opis</TableHead>
                    <TableHead className="text-right">Kwota</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.slice(0, 200).map((t) => {
                    const typeInfo = getTypeLabel(t.type);
                    return (
                      <TableRow key={t.id}>
                        <TableCell className="text-xs text-slate-500">
                          {new Date(t.created_at).toLocaleString("pl-PL")}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm font-medium">{t.user_name || "—"}</p>
                            <p className="text-xs text-slate-500">{t.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                        </TableCell>
                        <TableCell className="text-sm max-w-xs truncate">{t.description}</TableCell>
                        <TableCell className={`text-right font-mono font-bold ${t.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {t.amount >= 0 ? '+' : ''}{t.amount}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          </Card>
        </TabsContent>

        {/* ZALEGLOSCI */}
        <TabsContent value="overdue">
          <Card className="rounded-xl border-red-200">
            <CardHeader>
              <CardTitle className="text-red-700 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" /> Zaleglosci kredytowe
              </CardTitle>
            </CardHeader>
            <CardContent>
              {overdueWallets.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Brak zaleglosci</p>
              ) : (
                <div className="space-y-3">
                  {overdueWallets.map((w) => (
                    <div key={w.user_id} className="flex items-center justify-between p-4 rounded-xl bg-red-50 border border-red-200">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                          <Building className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-bold text-red-800">{w.company_name || w.user_name || w.user_email}</p>
                          <p className="text-sm text-red-600">{w.user_email} • NIP: {w.nip || "brak"}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-red-700">{w.credit_used} PLN</p>
                        <p className="text-xs text-red-500">{w.credit_overdue_days} dni po terminie</p>
                      </div>
                      <Button 
                        onClick={() => handleMarkPaid(w.user_id)}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        <Check className="w-4 h-4 mr-2" /> Oznacz jako splacone
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog korekty salda */}
      <Dialog open={adjustDialog} onOpenChange={setAdjustDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Korekta salda: {selectedWallet?.user_email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-center p-4 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-500">Aktualne saldo</p>
              <p className="text-3xl font-bold">{selectedWallet?.balance || 0} PLN</p>
            </div>
            <div className="space-y-2">
              <Label>Kwota korekty (+ lub -)</Label>
              <Input 
                type="number" 
                value={adjustAmount} 
                onChange={(e) => setAdjustAmount(parseInt(e.target.value) || 0)}
                className="text-center text-xl font-bold"
              />
            </div>
            <div className="space-y-2">
              <Label>Powod</Label>
              <Input 
                value={adjustReason} 
                onChange={(e) => setAdjustReason(e.target.value)}
                placeholder="np. Przelew bankowy, korekta bledu..."
              />
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-xl">
              <p className="text-sm text-blue-600">Nowe saldo</p>
              <p className="text-2xl font-bold text-blue-700">
                {(selectedWallet?.balance || 0) + adjustAmount} PLN
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustDialog(false)}>Anuluj</Button>
            <Button onClick={handleAdjust} disabled={adjusting || adjustAmount === 0}>
              {adjusting ? "Zapisywanie..." : "Zapisz korekte"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
